import * as d3 from 'd3-scale';
import * as d3Chromatic from 'd3-scale-chromatic';


export const COLD_PALETTE = [
   '#287CF6', // Sky blue
  '#22577E',
  '#00B4D8', // Light sky blue
  '#6C5B7B',
  '#355E3B',
  '#911F27',
   '#404E4D',  // Charcoal
   '#1D3C45',  // Deep navy
   '#009688',  // Teal
   '#283533',  //  Very dark gray
   '#33414E'  //  Dark muted green
];

function sampleFromColor(colorName, numSamples = 1) {
    // Define a mapping from color names to d3 chromatic scales
    const colorScales = {
        red: d3Chromatic.interpolateReds,
        blue: d3Chromatic.interpolateBlues,
        green: d3Chromatic.interpolateGreens
    };

    // Check if the provided colorName is valid
    if (!colorScales[colorName]) {
        throw new Error("Unsupported color name. Please use 'red', 'blue', or 'green'.");
    }

    // Get the appropriate color scale for the given color name
    const scale = d3.scaleSequential(colorScales[colorName]).domain([0, 1]);

    // Generate and return random color samples
    const samples = [];
    for (let i = 0; i < numSamples; i++) {
        const t = Math.random(); // Generate a random number between 0 and 1
        samples.push(scale(t));  // Map the random number to a color in the scale
    }

    return samples;
}

// Example usage


export const toRGB = (hex, opacity = 1) => {
        // Ensure the hex value is valid

        if (!/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
            hex = '#000000';
        }

        // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
        let c = hex.substring(1).split('');
        if (c.length === 3) {
            c = [c[0], c[0], c[1], c[1], c[2], c[2]];
        }
        c = '0x' + c.join('');

        // Generate the rgba color
        return 'rgba(' + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') + ',' + opacity + ')';
    }



export const getRandomColor = () => {
  const index = Math.floor(Math.random() * COLD_PALETTE.length);
  return COLD_PALETTE[index];
};

export const getRandomHexColor = () => {
    // Define color control by selectively adjusting RGB ranges
    const red = Math.floor(Math.random() * 256);
    const green = Math.floor(Math.random() * 160); // Lower green for deeper hues
    const blue = Math.floor(Math.random() * 256);

    // Logic to increase likelihood of desired color spectrums
    const colorType = Math.floor(Math.random() * 5); // Randomly choose a color type

    let modifiedRed, modifiedGreen, modifiedBlue;
    switch (colorType) {
        case 0: // Deep red
            modifiedRed = 100 + red % 156; // Ensures red is always pronounced
            modifiedGreen = green % 90; // Cap green for a deeper red
            modifiedBlue = blue % 90; // Keep blue low
            break;
        case 1: // Blue
            modifiedRed = red % 70; // Lower red
            modifiedGreen = green % 130; // Moderate green
            modifiedBlue = 120 + blue % 136; // Ensures blue is always pronounced
            break;
        case 2: // Green
            modifiedRed = red % 80; // Lower red
            modifiedGreen = 100 + green % 156; // Ensures green is pronounced
            modifiedBlue = blue % 80; // Lower blue
            break;
        case 3: // Blue-Purple
            modifiedRed = 80 + red % 176; // Pronounced red but not overwhelming
            modifiedGreen = green % 70; // Lower green
            modifiedBlue = 100 + blue % 156; // Ensures blue is pronounced
            break;
        case 4: // Blue-Gray
            const grayScale = 70 + blue % 90; // Ensures not too bright
            modifiedRed = grayScale; // Apply gray scale to all components
            modifiedGreen = grayScale;
            modifiedBlue = 120 + blue % 136; // Blue should still be pronounced
            break;
    }

    // Convert RGB to Hex
    const toHex = (c) => {
        const hex = c.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
    };

    let color = `#${toHex(modifiedRed)}${toHex(modifiedGreen)}${toHex(modifiedBlue)}`;

    return color;
};

export const generateRandomColor = ({ existingColors = [], type = 'hex' }) => {
    if (type !== 'hex') {
        throw new Error('Currently, only hex type is supported.');
    }

    let newColor;
    do {
        newColor = getRandomHexColor();
    } while (Array.isArray(existingColors) && existingColors.includes(newColor));

    return newColor;
};


export const COOL_COLOR_PALETTE = [
   '#0083ff', // Deep Indigo
    '#000',
    '#F43F5E', // Soft Red
  '#10B981', // Vibrant Teal Green
  '#22D3EE', // Light Aqua
  '#A855F7', // Vivid Purple
  '#F59E0B', // Warm Gold
  '#0F172A'  // Dark Navy for Backgrounds or Highlights
];
export const CLUSTER_COLORS = [
    '#6366F1',   // Indigo (a cool, vibrant blue)
    '#AD195A',   // Amber (a warm, energetic yellow)
    '#4ADE80',   // Lime (a fresh, zesty green)
    '#FB7185',   // Pink (a playful, softer red)
    '#A78BFA',   // Lavender (a muted, calming purple)
];

export const CATEGORICAL_COLORS = [
    '#0083ff', // Deep Indigo
    '#5C15C6',
  '#10B981', // Vibrant Teal Green
  '#22D3EE', // Light Aqua
        '#F43F5E', // Soft Red

  '#A855F7', // Vivid Purple
  '#F59E0B', // Warm Gold
    '#6366F1',   // Indigo (a cool, vibrant blue)
    '#AD195A',   // Amber (a warm, energetic yellow)
    '#4ADE80',   // Lime (a fresh, zesty green)
    '#FB7185',   // Pink (a playful, softer red)
    '#A78BFA',   // Lavender (a muted, calming purple)
]





export const mapListToColors = (list)=>{
    const colorMap = {}
    list.forEach((item, index)=>{
        colorMap[item] = COOL_COLOR_PALETTE[index < COOL_COLOR_PALETTE.length ? index : index % COOL_COLOR_PALETTE.length];
    });
    return colorMap;
}

/**
 * Darkens a hexadecimal color by a specified shade amount.
 *
 * @param {string} hex - The hexadecimal color code.
 * @param {number} shade - The amount to darken the color, default is 10.
 * @returns {string} - The darkened hexadecimal color code.
 */
export function darkenHexColor(hex, shade = 10) {
  // Ensure the input is a valid hex color
  if (!/^#([0-9A-F]{3}){1,2}$/i.test(hex)) {
    throw new Error('Invalid hex color');
  }

  // Convert hex to RGB
  let r = parseInt(hex.slice(1, 3), 16);
  let g = parseInt(hex.slice(3, 5), 16);
  let b = parseInt(hex.slice(5, 7), 16);

  // Subtract the shade from each component, ensuring it doesn't fall below 0
  r = Math.max(0, r - shade);
  g = Math.max(0, g - shade);
  b = Math.max(0, b - shade);

  // Convert RGB back to hex and return
  return `#${(r.toString(16).padStart(2, '0'))}${(g.toString(16).padStart(2, '0'))}${(b.toString(16).padStart(2, '0'))}`;
}

/**
 * Interpolates between two hexadecimal colors.
 *
 * @param {string} startHex - The starting hexadecimal color code.
 * @param {string} endHex - The ending hexadecimal color code.
 * @param {number} factor - The interpolation factor (0 to 1).
 * @returns {string} - The interpolated hexadecimal color code.
 */
export function interpolateHexColor(startHex, endHex, factor = 0.5) {
  if (!/^#([A-Fa-f0-9]{3}){1,2}$/.test(startHex) || !/^#([A-Fa-f0-9]{3}){1,2}$/.test(endHex)) {
    throw new Error('Invalid hex color');
  }

  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  const hexToRgb = (hex) => {
    let c = hex.substring(1).split('');
    if (c.length === 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]];
    }
    return [parseInt(c[0] + c[1], 16), parseInt(c[2] + c[3], 16), parseInt(c[4] + c[5], 16)];
  };

  const [r1, g1, b1] = hexToRgb(startHex);
  const [r2, g2, b2] = hexToRgb(endHex);

  const interpolate = (start, end, factor) => Math.round(start + (end - start) * factor);

  const r = interpolate(r1, r2, factor);
  const g = interpolate(g1, g2, factor);
  const b = interpolate(b1, b2, factor);

  const toHex = (c) => {
    const hex = c.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}


/**
 * Converts a hex color to an HSL object.
 *
 * @param {string} hex - The hex color code.
 * @returns {object} - The HSL representation of the color.
 */
function hexToHsl(hex) {
    let r = parseInt(hex.slice(1, 3), 16) / 255;
    let g = parseInt(hex.slice(3, 5), 16) / 255;
    let b = parseInt(hex.slice(5, 7), 16) / 255;

    let max = Math.max(r, g, b);
    let min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
        h = s = 0; // achromatic
    } else {
        let d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
    }

    return { h: h * 360, s: s * 100, l: l * 100 };
}

/**
 * Converts an HSL object to a hex color.
 *
 * @param {object} hsl - The HSL representation of the color.
 * @returns {string} - The hex color code.
 */
function hslToHex(hsl) {
    let { h, s, l } = hsl;
    s /= 100;
    l /= 100;

    let c = (1 - Math.abs(2 * l - 1)) * s;
    let x = c * (1 - Math.abs((h / 60) % 2 - 1));
    let m = l - c / 2;
    let r = 0, g = 0, b = 0;

    if (0 <= h && h < 60) { r = c; g = x; b = 0; }
    else if (60 <= h && h < 120) { r = x; g = c; b = 0; }
    else if (120 <= h && h < 180) { r = 0; g = c; b = x; }
    else if (180 <= h && h < 240) { r = 0; g = x; b = c; }
    else if (240 <= h && h < 300) { r = c; g = 0; b = x; }
    else if (300 <= h && h < 360) { r = x; g = 0; b = c; }

    r = Math.round((r + m) * 255).toString(16).padStart(2, '0');
    g = Math.round((g + m) * 255).toString(16).padStart(2, '0');
    b = Math.round((b + m) * 255).toString(16).padStart(2, '0');

    return `#${r}${g}${b}`;
}

/**
 * Steps a hex color by rotating it on the color wheel and adjusting its intensity.
 *
 * @param {string} hex - The hex color code.
 * @param {number} [rotation=0] - The degree of rotation on the color wheel.
 * @param {number} [intensity=0] - The adjustment to the color's intensity.
 * @returns {string} - The adjusted hex color code.
 */
export function stepHexColor(hex, rotation = 0, intensity = 0) {
    if (!/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
        throw new Error('Invalid hex color');
    }

    let hsl = hexToHsl(hex);
    hsl.h = (hsl.h + rotation) % 360;
    hsl.l = Math.min(100, Math.max(0, hsl.l + intensity));

    return hslToHex(hsl);
}

